/**
 * Taking a live AI call over in this browser (ring R6d): the half that is plain
 * data. What the call window looks like the moment the operator presses, and
 * what each way of it not working is called.
 *
 * React-free, so it runs under plain node. The half that talks to the Voice SDK
 * and the server is `useAiTakeover.ts`.
 */
import { ApiError } from "../../../lib/api";
import { IDLE, type CallState } from "../callState";

/** The AI call as the dock's card has it: enough to open the window on. */
export interface AiTakeoverTarget {
  callLogId: string;
  companyName: string | null;
  regCode: string | null;
  /** The number the AI's call is on. In test mode that is the test phone, not the company. */
  toNumber: string;
}

/**
 * The call window as it opens, before anything has been asked of the server.
 *
 * Opened at once rather than after the claim, so a second press, an offer
 * arriving, or the dial pad cannot slip in while the request is out: the phase
 * is what refuses all three. `conference` stays false - the row is not a
 * conference bridge, and hold and transfer are not offered on it.
 */
export function takeoverCallState(target: AiTakeoverTarget): CallState {
  return {
    ...IDLE,
    phase: "preparing",
    direction: "outbound",
    target: {
      phone: target.toNumber,
      companyName: target.companyName ?? undefined,
      regCode: target.regCode ?? undefined,
      source: "manual",
    },
    dialedNumber: target.toNumber || null,
    callLogId: target.callLogId,
    takeover: true,
  };
}

/** Refusals that mean the call is over, or was never one to take. */
const GONE = new Set(["not_found", "not_talking", "stopped", "gone"]);
/** Refusals that mean this call may not be taken over, whoever tries. */
const REFUSED = new Set(["not_yours", "forbidden", "machine", "record_objected"]);

/**
 * The window's error for a press the server did not take.
 *
 * Five sentences rather than the dock's sixteen: the browser route has no mobile
 * number to be missing or wrong, and the dock only offers the press when the
 * call could be taken, so everything else here is a race lost by a second.
 */
export function takeoverStartError(err: unknown): string {
  if (!(err instanceof ApiError)) return "takeover_failed";
  const code = err.message;
  if (code === "taken") return "takeover_taken";
  if (code === "opening") return "takeover_opening";
  if (GONE.has(code) || err.status === 404) return "takeover_gone";
  if (REFUSED.has(code) || err.status === 403) return "takeover_refused";
  return "takeover_failed";
}
